/* eslint-disable @typescript-eslint/member-ordering */
import { $t } from '@/i18n';
import { getAIPageContextInstance } from '@/utils/global/api';
import { MessagePlugin, DialogPlugin } from 'tdesign-vue';
import { postEmail, notifyByWework, notifyByWeworkCustomer } from '@/components/uicorePlugin/flowAtoms/notification';
import { ENABLE_EMAIL_SETTING, HAS_EMAIL_SERVICE } from '@/config/constant';
import { robotApi } from '@/services';
import { wujiFetchAIScf } from '../gogo-backend-runtime-logic/wujiFetchAIScf';
// DTS 相关逻辑已拆分到 dts/ 目录
export { getTypeDefinitions } from './dts';
export default class Wuji {
    getCurrentUser = () => {
        const w = getAIPageContextInstance();
        return {
            ...w.user,
            hasPermission: (permission) => w.hasPermission(permission),
            isRoleMemberOf: (role) => w.isRoleMemberOf(role),
        };
    };
    fetchCurrentUser = async () => {
        const w = getAIPageContextInstance();
        await w.initUserInfo();
        return {
            ...w.user,
            hasPermission: (permission) => w.hasPermission(permission),
            isRoleMemberOf: (role) => w.isRoleMemberOf(role),
        };
    };
    getGlobal = () => {
        const w = getAIPageContextInstance();
        return w.global;
    };
    fetchGlobalAndSecrets = () => {
        throw new Error('fetchGlobalAndSecrets is not supported in frontend');
    };
    get Sidebar() {
        const w = getAIPageContextInstance();
        return w.sidebar;
    }
    get ai() {
        const w = getAIPageContextInstance();
        return w.ai;
    }
    get env() {
        const w = getAIPageContextInstance();
        return w.env;
    }
    get Message() {
        return MessagePlugin;
    }
    get Confirm() {
        return DialogPlugin.confirm;
    }
    /**
     * 邮件通知（需要启用邮件服务）
     * @returns 邮件服务对象，如果服务未启用则返回 null
     */
    useEmail = () => {
        // 邮件服务未启用时返回 null，让用户可以优雅地处理
        if (!ENABLE_EMAIL_SETTING || !HAS_EMAIL_SERVICE) {
            return null;
        }
        return {
            /**
             * 发送邮件
             * @param to 收件人（多个用逗号/分号分隔）
             * @param title 邮件主题
             * @param content 邮件内容
             */
            send: async (params) => {
                const { from, to, title, content, cc = '', bcc = '' } = params;
                if (!to || !title || !content) {
                    throw new Error($t('k_a765637d'));
                }
                await postEmail(to, title, content, { cc, bcc, emailId: from || '' });
            },
        };
    };
    /**
     * 企业微信相关功能
     */
    useWxwork = () => ({
        /**
         * 机器人通知
         * @param robotId 机器人ID
         * @param message 消息内容（JSON格式，参考企微机器人API）
         */
        sendRobotMessage: async (params) => {
            const { projectId, env } = window.w;
            const { robotId, message } = params;
            if (!robotId || !message) {
                throw new Error($t('k_c1767c7'));
            }
            let data;
            if (typeof message === 'string') {
                try {
                    data = JSON.parse(message);
                }
                catch {
                    throw new Error($t('k_a6543aad'));
                }
            }
            else {
                data = message;
            }
            await robotApi.postRobotMessage(projectId, env, robotId, data);
        },
        /**
         * 应用消息通知
         * @param receivers 接收人（多个用逗号/分号分隔）
         * @param type 消息类型：text/markdown/textcard
         * @param content 消息内容（text/markdown类型使用）
         * @param weworkApp 通知应用：'builtin' 使用平台内置应用，'custom' 使用应用自定义配置
         */
        sendAppMessage: async (params) => {
            const { projectId, env } = window.w;
            const { receivers, type: msgType, content, title, description, url, buttonText, weworkApp = 'builtin' } = params;
            if (!receivers) {
                throw new Error($t('k_7fbdc837'));
            }
            if (['text', 'markdown'].includes(msgType) && !content) {
                throw new Error($t('k_a8f78fff'));
            }
            if (msgType === 'textcard' && (!title || !description || !url)) {
                throw new Error($t('k_ac7aa0b1'));
            }
            const receiverList = receivers.split(/[ ,;]/g).filter(Boolean);
            const data = {
                receivers: receiverList,
                msgType,
                data: ['text', 'markdown'].includes(msgType)
                    ? { content }
                    : { title, description, url, btntxt: buttonText || $t('k_64babb9a') },
            };
            const builtin = weworkApp === 'builtin';
            await notifyByWework(projectId, env, data, builtin);
        },
        /**
         * 客服号消息通知
         * @param receivers 接收人（多个用逗号/分号分隔）
         * @param type 消息类型：text/markdown
         * @param content 消息内容
         */
        sendCustomerMessage: async (params) => {
            const { projectId, env } = window.w;
            const { receivers, type: msgType, content } = params;
            if (!receivers) {
                throw new Error($t('k_7fbdc837'));
            }
            if (!['text', 'markdown'].includes(msgType)) {
                throw new Error($t('k_8d2306ed'));
            }
            if (!content) {
                throw new Error($t('k_a8f78fff'));
            }
            const receiverList = receivers.split(/[ ,;]/g).filter(Boolean);
            await notifyByWeworkCustomer(projectId, env, {
                receivers: receiverList,
                msgType,
                data: { content },
            });
        },
    });
    /**
     * 全局弹窗（页面类型为「弹窗」的全局弹窗）
     * @returns open / hide，对应运行时上下文中的 showGlobalModal / hideGlobalModal
     */
    useGlobalModal = () => {
        const w = getAIPageContextInstance();
        return {
            open: (id, data, opt) => w.showGlobalModal(id, data, { keepAlive: false, suffix: '', ...opt }),
            hide: (id, data, opt) => w.hideGlobalModal(id, data, { destroy: false, ...opt }),
        };
    };
    // 支持 import wuji from '@wujibase/wuji' 的形式
    get default() {
        return this;
    }
    // eslint-disable-next-line @typescript-eslint/member-ordering, @typescript-eslint/naming-convention
    wujiFetch = wujiFetchAIScf;
}
